import { mobileAccess, processViewPartHtml, virtualDir } from "#skyward_global";
import { encodeHtml, SequentialOperationHandler } from "./skyward.base.extensions";
import { DependencySnapshot, getDependencyDataWithoutWaiting, getRequestDataHash, HashTransaction, TextBoxData, waitForDependencies } from "./skyward.base.requestDataHash";
import { getScopeContainer } from "./skyward.ui.controls";
import { createEel, EEL, EELOptions, setFocusToTextBoxOnClick, UserInputOptions } from "./skyward.ui.eel";
import { registerEvents } from "./skyward.ui.events";
import { notifyDOMLoad } from "./skyward.ui.window";
import { JQueryInput } from "./types";

export interface TileViewEELSearchData extends TextBoxData
{
    area: string;
    controller: string;
    templateName: string;
    filter: object;
    postdata: object;
    queryParameterData: object | null;
    requestDataHashSrc: string;
    requestDataHash: string;
    dynamicFilterHashes?: Record<string, string | null>;
    originalValue: string | number;
}

export interface TileViewEELPostData extends Record<string, unknown>
{
    area?: string,
    controller?: string,
    templateName?: string,
    filter?: string,
    postData?: string,
    pageStateControlValues?: string,
    searchText?: string,
    dependency?: string,
    id?: any,
    includeSubsequentRecords?: boolean
}

export interface TileViewEELObject
{
    tileViewEEL: JQuery<HTMLElement>;
    hiddenValue: JQuery<HTMLElement>;
    searchBox: JQuery<HTMLElement>;
    searchButton: JQuery<HTMLElement>;
    eel: EEL;
}

interface TemplatedResultsResponse
{
    toolbarHtml: string;
    selectedItemId: string;
    templatedItems: string[];
    templatedItemHashes: string[];
}

interface TileViewEELValueData
{
    updateBoxes: (selectedID?: string | number | null, suppressChangeEvent?: boolean) => Promise<void>;
    originalValue: string | number;
    originalSelectedValueHash?: string;
    selectedValueHash?: string;
}

interface TileViewEELParts
{
    value: JQuery<HTMLElement>;
    searchBox: JQuery<HTMLElement>;
}

interface AjaxData extends Record<string, unknown>
{
    search?: string | number | string[];
}

const skyward = window.skyward;

const events =
{
    init
};

registerEvents("tileViewEEL", events);

export function registerTileViewEEL(origTileViewEEL: JQueryInput, customInputOptions?: Partial<UserInputOptions>, customEelOptions?: Partial<EELOptions>)
{
    const tileViewEEL = $(origTileViewEEL);
    const tileViewEELValue = tileViewEEL.find(".tileViewEEL_value") as JQuery<HTMLInputElement>
    const searchBox = tileViewEEL.find(".tileViewEEL_search");
    const searchButton = tileViewEEL.find(".tileViewEEL_searchButton");
    const searchData = searchBox.data() as TileViewEELSearchData;
    let eel: EEL;
    const popupID = searchBox.attr("id") + "_popup";
    const singleRequestHandler = new SequentialOperationHandler();
    const transaction = new HashTransaction(tileViewEELValue);
    const tileViewEELValueData = tileViewEELValue.data() as TileViewEELValueData;
    tileViewEELValueData.updateBoxes = updateBoxes;

    let userInput: UserInputOptions =
    {
        input: searchBox,
        button: searchButton,
        customKeyDownHandler: keyDownHandler
    };
    userInput = $.extend(userInput, customInputOptions);

    if (!mobileAccess)
    {
        setFocusToTextBoxOnClick(tileViewEEL);
    }

    const label = tileViewEEL.findLabel();
    if (label.length)
    {
        label.attr("for", searchBox.addIds().attr("id")!);
    }

    let eelOptions: Partial<EELOptions> =
    {
        key: tileViewEEL,
        valueInput: tileViewEELValue,
        userInputs: [userInput],
        getSelectionListContainer: function ()
        {
            const popupContent = $("<div />", { id: popupID, 'class': "tileViewEELModalContainer anchored popup noAnchoredTail" });
            popupContent.on("click", ".templatedResult:not(.ignoreClickEvent)", function ()
            {
                const tile = $(this);

                const selectedRow = $("#" + popupID).find(".templatedResult.selected");
                selectedRow.removeClass("selected");
                tile.addClass("selected");

                eel.selectCurrentHighlightedValue();
            });

            return popupContent;
        },
        getSelectionList: function (includeSubsequentRecords)
        {
            const deferred = $.Deferred();

            // TODO (6756543) wait for dependencies before searching

            $.ajax(
                {
                    url: `${virtualDir}Common/TileViewEEL/ViewSearchResults/`,
                    type: "POST",
                    dataType: "JSON",
                    activityIndicator: { hideGlass: true },
                    data: getPostData(searchData, searchBox, tileViewEELValue.val() as string, includeSubsequentRecords),
                    success: async function (data: TemplatedResultsResponse)
                    {
                        const popupContent = $("<div>", { class: "columnLayoutWrapper--tileViewEEL" });

                        if (data.toolbarHtml)
                        {
                            // Process HTML through processViewPartHtml to handle any script tags
                            const processedToolbarHtml = await processViewPartHtml(data.toolbarHtml);
                            const toolbar = $("<div>", { class: "toolbar--browseControls tileviewEEL" }).append(processedToolbarHtml);
                            popupContent.append(toolbar);
                        }

                        const recordItems = $("<div>", { role: "listbox" });

                        // Give the records list the same label as the EELs search box input
                        const searchBoxLabel = searchBox.attr("aria-labelledby");
                        if (searchBoxLabel)
                        {
                            recordItems.attr("aria-labelledby", searchBoxLabel);
                        }

                        // Add the templated results to the div
                        if (data.templatedItems.length > 0)
                        {
                            // jquery.data() would automatically deserialize json data
                            const localSelectedKey = tileViewEEL.attr("data-selected-item-key");

                            let selectedItem = null;
                            let foundServerSelectedKey = false;

                            const idPrefix = tileViewEELValue.attr("id") + "-item-";

                            // Process each templated item through processViewPartHtml
                            const templatedItems = [];
                            for (const itemHtml of data.templatedItems)
                            {
                                const processedHtml = await processViewPartHtml(itemHtml);
                                templatedItems.push($(processedHtml));
                            }

                            let itemIndex = -1;
                            for (const templatedItem of templatedItems)
                            {
                                itemIndex++;
                                const resultKey = templatedItem.attr("data-id");
                                const resultId = idPrefix + resultKey;
                                templatedItem.data("hash", data.templatedItemHashes[itemIndex]);

                                const labelArea = templatedItem.find(".js-labelArea");
                                if (labelArea.length)
                                {
                                    const labelId = resultId + "-label";
                                    labelArea.attr("id", labelId);
                                    templatedItem.attr("aria-labelledby", labelId);
                                }

                                const descriptionArea = templatedItem.find(".js-descriptionArea");
                                if (descriptionArea.length)
                                {
                                    const descriptionId = resultId + "-description";
                                    descriptionArea.attr("id", descriptionId);
                                    templatedItem.attr("aria-describedby", descriptionId);
                                }

                                if (!foundServerSelectedKey)
                                {
                                    if (resultKey === data.selectedItemId)
                                    {
                                        selectedItem = templatedItem;
                                        foundServerSelectedKey = true;
                                    }
                                    else if (selectedItem === null || resultKey === localSelectedKey)
                                    {
                                        selectedItem = templatedItem;
                                    }
                                }

                                recordItems.append(templatedItem);
                            }

                            if (selectedItem)
                            {
                                selectedItem.addClass("selected");
                                searchBox.attr("aria-activedescendant", selectedItem.addIds().attr("id")!);
                            }
                        }
                        else
                        {
                            const noRecords = $("<p>", { text: "No records to display" });
                            const noRecordsItem = $("<div>", { class: "noRecordsInBrowse browseRow templatedResult ignoreClickEvent selectorElementsCenter" }).append(noRecords);
                            recordItems.append(noRecordsItem);
                        }

                        popupContent.append(recordItems);

                        deferred.resolve(popupContent);
                    },
                    error: function ()
                    {
                        deferred.reject();
                    }
                });

            return deferred.promise();
        },
        selectionListPositioningOptions:
        {
            getRows: function () { return $("#" + popupID).find('.templatedResult, .toolbar--browseControls.tileviewEEL'); },
            getHeaderRow: function () { return $("#" + popupID).find('.templatedResult').first(); }
        },
        setCurrentEnteredValue: function (_input)
        {
            return singleRequestHandler.run((signal) => transaction.run(signal, async () =>
            {
                const dependencies = await waitForDependencies(searchBox.data() as TileViewEELSearchData, getScopeContainer(searchBox), signal);

                await $.ajax(
                    {
                        url: `${virtualDir}Common/TileViewEEL/ValidateText/`,
                        type: "POST",
                        dataType: "JSON",
                        data: getPostData(searchData, searchBox, undefined, undefined, dependencies),
                        signal: signal,
                        success: function (data)
                        {
                            if (!data.isValid)
                            {
                                tileViewEELValue.data().selectedValueHash = "";
                                updateBoxes(null);
                                return;
                            }

                            populateBoxes(tileViewEEL, data, data.id);
                            tileViewEEL.trigger('eelComplete');
                        },
                        error: function ()
                        {
                            if (!signal.aborted)
                            {
                                updateBoxes(null);
                            }
                        }
                    });
            }));
        },
        setCurrentHighlightedValue: function ()
        {
            const selectedRow = $("#" + popupID).find(".templatedResult.selected");
            // Selected ID can be a JSON object, which we want to pass through verbatim without JQuery parsing it
            const selectedRowId = selectedRow.attr("data-id");
            updateBoxes(selectedRowId);
        },
        clearValues: function ()
        {
            if (!searchBox.val())
            {
                updateBoxes(null);
            }
            else
            {
                // TODO This is called when clicking away from an eel without selecting a new value. Can we avoid the ajax request in this case?
                updateBoxes(tileViewEELValue.val() as string);
            }
        }
    };
    // remove _value suffix present on regular tileViewEELs but not customization ForeignKeyField eels
    const name = (tileViewEELValue.data("original-name") ?? tileViewEELValue.attr("name")).replace(/_value$/, "");
    if (!tileViewEELValue[0].readOnly)
    {
        loadRequestDataHashes(searchData.requestDataHashSrc, name, searchData, tileViewEELValue.data() as TileViewEELValueData);
    }
    eelOptions = $.extend(eelOptions, customEelOptions);

    eelOptions.onSelectionListLoad = function ()
    {
        const popupContent = $("#" + popupID);
        const selectedRow = popupContent.find(".templatedResult.selected");

        const additionalTileViewEELPadding = 2;
        popupContent.outerHeight(popupContent.outerHeight()! + additionalTileViewEELPadding);

        scrollIntoView(selectedRow);
        notifyDOMLoad(popupContent);

        customEelOptions?.onSelectionListLoad?.();
    };

    eelOptions.beforeOpen = function ()
    {
        searchBox.attr("aria-expanded", "true");
        searchBox.attr("aria-controls", popupID);
        searchButton.attr("aria-controls", popupID);
        customEelOptions?.beforeOpen?.();
    };

    eelOptions.beforeClose = function ()
    {
        searchBox.attr("aria-expanded", "false");
        searchBox.removeAttr("aria-controls");
        searchButton.removeAttr("aria-controls");
        customEelOptions?.beforeClose?.();
    };

    eel = createEel(eelOptions);
    tileViewEEL.addClass("eventsBound");
    tileViewEELValue.trigger("controlLoaded");

    const obj: TileViewEELObject =
    {
        tileViewEEL,
        hiddenValue: tileViewEELValue,
        searchBox,
        searchButton,
        eel
    };

    return obj;

    function keyDownHandler(event: KeyboardEvent)
    {
        const selectedRow = $("#" + popupID).find(".templatedResult.selected");
        if (selectedRow.length === 0)
        {
            return;
        }

        const eventKeys = skyward.keys.getKeyNamesFromEvent(event);

        const goUp = eventKeys.ARROW_LEFT || eventKeys.ARROW_UP;
        const goDown = eventKeys.ARROW_RIGHT || eventKeys.ARROW_DOWN;

        if (!goUp && !goDown)
        {
            return;
        }

        let nextRow;
        if (goUp)
        {
            nextRow = selectedRow.prevAll(".templatedResult").not(".js-nonSelectableOption").first();
        }
        else
        {
            nextRow = selectedRow.nextAll(".templatedResult").not(".js-nonSelectableOption").first();
        }

        updateSelectedRow(selectedRow, nextRow);

        event.preventDefault();
        return false;
    }

    function updateSelectedRow(selectedRow: JQuery<HTMLElement>, nextRow?: JQuery<HTMLElement>)
    {
        const popupContent = $("#" + popupID);

        if (!nextRow || nextRow.length === 0 || popupContent.length === 0)
        {
            return;
        }

        selectedRow.removeClass("selected");
        nextRow.addClass("selected");
        searchBox.attr("aria-activedescendant", nextRow.addIds().attr("id")!);

        scrollIntoView(nextRow);
    }

    function scrollIntoView(row: JQuery<HTMLElement>)
    {
        row.scrollintoview(
            {
                duration: 0,
                direction: "vertical",
                viewPadding: { y: 0 }
            });
    }

    function updateBoxes(selectedID?: string | number | null)
    {
        return singleRequestHandler.run(async function (signal)
        {
            if (!selectedID)
            {
                tileViewEELValue.data().selectedValueHash = "";
                populateBoxes(tileViewEEL, { search: "" }, "");
                return;
            }
            if (selectedID == tileViewEELValueData.originalValue)
            {
                populateBoxes(tileViewEEL,
                    {
                        search: searchData.originalValue,
                        selectedValueHash: tileViewEELValueData.originalSelectedValueHash,
                    },
                    selectedID);
                return;
            }

            const dependencies = await waitForDependencies(searchBox.data() as TileViewEELSearchData, getScopeContainer(searchBox), signal);

            await $.ajax(
                {
                    url: `${virtualDir}Common/TileViewEEL/ValidateSelection/`,
                    type: "POST",
                    dataType: "JSON",
                    data: getPostData(searchData, searchBox, selectedID, undefined, dependencies),
                    signal: signal,
                    success: function (data)
                    {
                        if (signal.aborted) { return; }
                        populateBoxes(tileViewEEL, data, selectedID);
                        tileViewEEL.trigger('eelComplete');
                    },
                    error: function ()
                    {
                        if (!signal.aborted)
                        {
                            populateBoxes(tileViewEEL, { search: "" }, "");
                        }
                    }
                }
            );
        });
    }
}

export function getParts(tileViewEel: JQuery<HTMLElement>)
{
    const parts: TileViewEELParts =
    {
        value: tileViewEel.find('.tileViewEEL_value'),
        searchBox: tileViewEel.find('.tileViewEEL_search')
    };

    return parts;
}

export function populateBoxes(tileViewEEL: JQuery<HTMLElement>, ajaxData: AjaxData, id: string | number, suppressChangeEvent = false)
{
    const searchBox = tileViewEEL.find(".tileViewEEL_search");
    searchBox.val(ajaxData.search || "");
    searchBox.data().currentValue = ajaxData.search || "";

    const tileViewEELValue = tileViewEEL.find(".tileViewEEL_value");
    tileViewEELValue.val(id);
    tileViewEELValue.data().selectedValueHash = ajaxData.selectedValueHash ?? "";
    if (!suppressChangeEvent)
    {
        tileViewEELValue.trigger("change");
    }
}

export function getPostData(searchData: TileViewEELSearchData, searchBox: JQuery<HTMLElement>, selectedID?: string | number | null, includeSubsequentRecords?: boolean,
    dependencies: DependencySnapshot = getDependencyDataWithoutWaiting(searchBox.data() as TileViewEELSearchData, getScopeContainer(searchBox)))
{
    const postData: TileViewEELPostData =
    {
        requestDataHash: searchData.requestDataHash,
        area: searchData.area,
        controller: searchData.controller,
        templateName: searchData.templateName,
        postData: JSON.stringify({ ...searchData.postdata, filter: JSON.stringify(searchData.filter) }),
        pageStateControlValues: searchBox.attr("name"),
        searchText: encodeHtml(searchBox.val() as string),
        dynamicFilterHashes: JSON.stringify(searchData.dynamicFilterHashes),
        dynamicFilterValues: JSON.stringify(dependencies),
        id: selectedID,
        includeSubsequentRecords: includeSubsequentRecords,
        queryParameterData: JSON.stringify(searchData.queryParameterData)
    };

    return postData;
}

export function copyValue(sourceElem: JQuery<HTMLElement>, destElem: JQuery<HTMLElement>, suppressChangeEvent = false)
{
    const sourceSearchBox = sourceElem.find(".tileViewEEL_search");
    const sourceTileViewEELValue = sourceElem.find(".tileViewEEL_value");
    const hash = sourceTileViewEELValue.data("selectedValueHash");
    const ajaxData =
    {
        search: sourceSearchBox.val(),
        selectedValueHash: hash,
    };
    const id = sourceTileViewEELValue.val() as number;
    populateBoxes(destElem.closest(".tileViewEEL"), ajaxData, id, suppressChangeEvent);
}

export async function setValue(tileViewEEL: JQueryInput, value?: string | number | null, suppressChangeEvent = false)
{
    const element = $(tileViewEEL);
    const tileViewEELValue = element.find(".tileViewEEL_value");

    // When clearing the value, populateBoxes needs to be called directly. `updateBoxes` is bound to the original tileViewEEL and will clear the original value.
    if (value === null || value === undefined || (typeof value === "string" && value.trim() === ""))
    {
        tileViewEELValue.data().selectedValueHash = "";
        populateBoxes(element, { search: "" }, "", suppressChangeEvent);
        return;
    }

    const tileViewEELValueData = tileViewEELValue.data() as TileViewEELValueData;
    await tileViewEELValueData.updateBoxes(value, suppressChangeEvent);
    if (!suppressChangeEvent)
    {
        tileViewEELValue.trigger("change");
    }
}

function loadRequestDataHashes(requestDataHashSrc: string, eelId: string, searchData: TileViewEELSearchData, hiddenData: TileViewEELValueData)
{
    const requestData = getRequestDataHash(requestDataHashSrc, eelId);
    if (!requestData)
    {
        return;
    }

    searchData.requestDataHash = requestData.hash;
    searchData.dynamicFilterHashes = { ...requestData.dependencies };
    hiddenData.originalSelectedValueHash = requestData.initialValueHash ?? "";
    hiddenData.selectedValueHash = requestData.initialValueHash ?? "";
}

function init(selector: JQueryInput)
{
    const element = $(selector);
    if (element.hasClass("eventsBound"))
    {
        return;
    }

    registerTileViewEEL(selector);
}
